// frontend/src/pages/AccountLedger/AccountLedger.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  ArrowLeft, Printer, Download, Loader2, AlertCircle,
  RefreshCw, Filter, Search, X, History, FileText, Calendar,
  Hash, User
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./AccountLedger.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const AccountLedger = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState({
    start_date: "",
    end_date: ""
  });

  const [appliedFilter, setAppliedFilter] = useState({
    start_date: "",
    end_date: ""
  });

  const [showJournalModal, setShowJournalModal] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [loadingJournal, setLoadingJournal] = useState(false);
  const [journalError, setJournalError] = useState("");

  // ✅ Format currency using the account's currency if available, otherwise company currency
  const formatAccountCurrency = useCallback((amount, accountCurrency = null) => {
    let currencyCode = accountCurrency || companyCurrency;
    // Ensure it's a valid 3-letter code
    if (typeof currencyCode !== "string" || !/^[A-Z]{3}$/.test(currencyCode)) {
      currencyCode = companyCurrency;
    }
    return formatCurrency(amount, currencyCode);
  }, [companyCurrency, formatCurrency]);

  const fetchAccountDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/account/${id}`, {
        headers: getAuthHeaders(),
      });

      if (response.data.success) setAccount(response.data.data);
      else toast.error(response.data.message || "Failed to load account details");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load account details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAccountTransactions = useCallback(async () => {
    try {
      setLoadingTransactions(true);

      const response = await axios.get(
        `${url}/api/account/transactions/by-account/${id}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (!response.data.success) {
        toast.error(
          response.data.message || "Failed to fetch transactions"
        );
        setTransactions([]);
        return;
      }

      const rows = Array.isArray(response.data.data)
        ? response.data.data
        : response.data.data?.transactions || [];

      const getDetailId = (transaction) =>
        transaction?._id ||
        transaction?.journal_transaction_detail_id ||
        transaction?.detail_id ||
        "";

      const getTransactionDate = (transaction) =>
        transaction.transaction_date ||
        transaction.date ||
        transaction.journal_transaction_id?.transaction_date ||
        transaction.created_at ||
        transaction.createdAt;

      const getCreatedDate = (transaction) =>
        transaction.created_at ||
        transaction.createdAt ||
        transaction.journal_transaction_id?.created_at ||
        transaction.journal_transaction_id?.createdAt ||
        getTransactionDate(transaction);

      const toTimestamp = (value) => {
        const timestamp = new Date(value).getTime();
        return Number.isFinite(timestamp) ? timestamp : 0;
      };

      const sortedTransactions = rows
        .map((transaction, originalIndex) => ({
          transaction,
          originalIndex
        }))
        .sort((left, right) => {
          const idA = String(getDetailId(left.transaction) || "");
          const idB = String(getDetailId(right.transaction) || "");

          if (idA && idB && idA !== idB) {
            return idA.localeCompare(idB);
          }

          if (idA && !idB) return -1;
          if (!idA && idB) return 1;

          const dateA = toTimestamp(getTransactionDate(left.transaction));
          const dateB = toTimestamp(getTransactionDate(right.transaction));

          if (dateA !== dateB) {
            return dateA - dateB;
          }

          const createdA = toTimestamp(getCreatedDate(left.transaction));
          const createdB = toTimestamp(getCreatedDate(right.transaction));

          if (createdA !== createdB) {
            return createdA - createdB;
          }

          return left.originalIndex - right.originalIndex;
        })
        .map(({ transaction }) => transaction);

      setTransactions(sortedTransactions);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch transactions"
      );
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAccountDetails();
    fetchAccountTransactions();
  }, [fetchAccountDetails, fetchAccountTransactions]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    if (
      filter.start_date &&
      filter.end_date &&
      filter.start_date > filter.end_date
    ) {
      toast.error("Start date cannot be after end date");
      return;
    }

    setAppliedFilter({ ...filter });
  };

  const resetFilters = () => {
    const emptyFilter = {
      start_date: "",
      end_date: ""
    };

    setFilter(emptyFilter);
    setAppliedFilter(emptyFilter);
    setSearchTerm("");
  };

  const handleBack = () => navigate("/ViewAccounts");
  const handlePrint = () => window.print();
  const handleDownloadPDF = async () => toast.info("PDF download feature coming soon");

  const getAccountTypeName = () => {
    const rawType =
      account?.type?.name ||
      account?.type?.accounting_type ||
      account?.account_type?.name ||
      account?.account_type?.accounting_type ||
      account?.accounting_type_id?.name ||
      account?.accounting_type_id?.accounting_type ||
      account?.account_type ||
      account?.accountType ||
      account?.type ||
      "";

    return String(rawType).trim().toLowerCase();
  };

  const getAccountTypeDisplay = () => {
    const accountType = getAccountTypeName();

    if (!accountType) return "General Ledger Account";

    return accountType
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") + " Account";
  };

  const getNormalBalance = () => {
    const configuredNormalBalance = String(
      account?.normal_balance ||
      account?.accountingType?.normal_balance ||
      account?.accounting_type?.normal_balance ||
      account?.accounting_type_id?.normal_balance ||
      account?.type?.normal_balance ||
      ""
    )
      .trim()
      .toUpperCase();
  
    if (
      configuredNormalBalance === "CR" ||
      configuredNormalBalance === "CREDIT"
    ) {
      return "CR";
    }
  
    if (
      configuredNormalBalance === "DR" ||
      configuredNormalBalance === "DEBIT"
    ) {
      return "DR";
    }
  
    const systemRole = String(
      account?.system_account_role || ""
    )
      .trim()
      .toUpperCase();
  
    if (
      systemRole === "SALES_RETURN" ||
      systemRole === "SALES_DISCOUNT"
    ) {
      return "DR";
    }
  
    if (systemRole === "PURCHASE_DISCOUNT") {
      return "CR";
    }
  
    const accountType = getAccountTypeName();
  
    return ["liability", "equity", "income", "revenue"].some(
      (type) =>
        accountType === type ||
        accountType.includes(type)
    )
      ? "CR"
      : "DR";
  };

  const getNaturalMovement = (debit, credit) => {
    const debitAmount = Number(debit) || 0;
    const creditAmount = Number(credit) || 0;

    return getNormalBalance() === "CR"
      ? creditAmount - debitAmount
      : debitAmount - creditAmount;
  };

  const formatNaturalBalance = (amount) => {
    const numericAmount = Number(amount) || 0;
    const normalSide = getNormalBalance();

    if (Math.abs(numericAmount) < 0.005) {
      return `${formatAccountCurrency(0)} ${normalSide}`;
    }

    const displayedSide =
      numericAmount >= 0
        ? normalSide
        : normalSide === "DR"
          ? "CR"
          : "DR";

    return `${formatAccountCurrency(Math.abs(numericAmount))} ${displayedSide}`;
  };

  const getBalanceClass = (amount) => {
    const numericAmount = Number(amount) || 0;

    if (Math.abs(numericAmount) < 0.005) {
      return "zero";
    }

    return numericAmount < 0 ? "abnormal" : "normal";
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    } catch {
      return "Invalid date";
    }
  };

  const getJournalId = (transaction) => {
    const journal = transaction?.journal_transaction_id;

    if (journal && typeof journal === "object") {
      return journal._id || journal.id || "";
    }

    return journal || transaction?.journal_id || "";
  };

  const normalizeJournalResponse = (data, fallbackTransaction) => {
    const payload = data?.data || data || {};
    const journal =
      payload.journal ||
      payload.transaction ||
      payload.header ||
      payload;

    const details =
      payload.details ||
      payload.lines ||
      payload.entries ||
      journal.details ||
      journal.lines ||
      journal.entries ||
      [];

    return {
      ...journal,
      details: Array.isArray(details) ? details : [],
      transaction_no:
        journal.transaction_no ||
        fallbackTransaction?.transaction_no ||
        fallbackTransaction?.journal_transaction_id?.transaction_no ||
        fallbackTransaction?.reference_no ||
        fallbackTransaction?.reference_id ||
        "-",
      reference_no:
        journal.reference_no ||
        fallbackTransaction?.reference_no ||
        fallbackTransaction?.journal_transaction_id?.reference_no ||
        fallbackTransaction?.reference_id ||
        "-",
      transaction_date:
        journal.transaction_date ||
        fallbackTransaction?.transaction_date ||
        fallbackTransaction?.journal_transaction_id?.transaction_date ||
        fallbackTransaction?.created_at,
      description:
        journal.description ||
        fallbackTransaction?.description ||
        fallbackTransaction?.journal_transaction_id?.description ||
        "-",
      posted_by:
        journal.posted_by_name ||
        journal.posted_by_display ||
        journal.posted_by ||
        journal.created_by?.Usercomp_sure_name ||
        journal.created_by?.name ||
        fallbackTransaction?.created_by?.Usercomp_sure_name ||
        "System"
    };
  };

  const handleReferenceClick = async (transaction) => {
    const journalId = getJournalId(transaction);

    if (!journalId) {
      toast.error("Journal transaction ID was not found for this reference");
      return;
    }

    try {
      setLoadingJournal(true);
      setJournalError("");
      setSelectedJournal(null);
      setShowJournalModal(true);

      const response = await axios.get(
        `${url}/api/account/journal/${journalId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message || "Failed to load journal details"
        );
      }

      setSelectedJournal(
        normalizeJournalResponse(response.data, transaction)
      );
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load journal details";

      setJournalError(message);
      toast.error(message);
    } finally {
      setLoadingJournal(false);
    }
  };

  const closeJournalModal = () => {
    setShowJournalModal(false);
    setSelectedJournal(null);
    setJournalError("");
  };

  const getJournalLineAccountName = (line) =>
    line?.account_id?.account_name ||
    line?.account?.account_name ||
    line?.account_name ||
    "Unknown Account";

  const getJournalLineAccountCode = (line) =>
    line?.account_id?.account_code ||
    line?.account?.account_code ||
    line?.account_code ||
    "";

  const getJournalTotals = () => {
    const details = selectedJournal?.details || [];

    return details.reduce(
      (totals, line) => ({
        debit: totals.debit + (Number(line?.debit) || 0),
        credit: totals.credit + (Number(line?.credit) || 0)
      }),
      { debit: 0, credit: 0 }
    );
  };

  const getTransactionDateValue = (transaction) =>
    transaction.transaction_date ||
    transaction.date ||
    transaction.journal_transaction_id?.transaction_date ||
    transaction.created_at ||
    transaction.createdAt;

  const getTransactionAmounts = (transaction) => {
    const journal =
      transaction.journal_transaction_id &&
        typeof transaction.journal_transaction_id === "object"
        ? transaction.journal_transaction_id
        : {};

    let debit = Number(transaction.debit) || 0;
    let credit = Number(transaction.credit) || 0;

    if (debit === 0 && credit === 0) {
      const amount = Number(transaction.amount) || 0;
      const type = String(
        transaction.transaction_type ||
        journal.transaction_type ||
        ""
      ).toLowerCase();

      const debitTypes = [
        "sale",
        "customer_payment",
        "opening_balance",
        "transfer_in",
        "expense_refund",
        "balance_adjustment_in",
        "sales_return",
        "sale_return",
        "sales_returns",
        "customer_return"
      ];

      const creditTypes = [
        "purchase",
        "supplier_payment",
        "transfer_out",
        "expense",
        "balance_adjustment_out"
      ];

      if (debitTypes.includes(type)) debit = amount;
      if (creditTypes.includes(type)) credit = amount;
    }

    return {
      debit,
      credit
    };
  };

  const toStartOfDay = (dateString) => {
    if (!dateString) return null;

    const value = new Date(`${dateString}T00:00:00`);
    return Number.isNaN(value.getTime()) ? null : value;
  };

  const toEndOfDay = (dateString) => {
    if (!dateString) return null;

    const value = new Date(`${dateString}T23:59:59.999`);
    return Number.isNaN(value.getTime()) ? null : value;
  };

  const datedTransactions = useMemo(() => {
    const startDate = toStartOfDay(appliedFilter.start_date);
    const endDate = toEndOfDay(appliedFilter.end_date);

    return transactions.filter((transaction) => {
      const date = new Date(getTransactionDateValue(transaction));

      if (Number.isNaN(date.getTime())) return false;
      if (startDate && date < startDate) return false;
      if (endDate && date > endDate) return false;

      return true;
    });
  }, [transactions, appliedFilter]);

  const balanceForward = useMemo(() => {
    const startDate = toStartOfDay(appliedFilter.start_date);

    if (!startDate) return 0;

    return transactions.reduce((balance, transaction) => {
      const date = new Date(getTransactionDateValue(transaction));

      if (Number.isNaN(date.getTime()) || date >= startDate) {
        return balance;
      }

      const { debit, credit } = getTransactionAmounts(transaction);
      return balance + getNaturalMovement(debit, credit);
    }, 0);
  }, [transactions, appliedFilter.start_date, account]);

  const ledgerData = useMemo(() => {
    let runningBalance = balanceForward;

    const rows = datedTransactions.map((transaction, index) => {
      const journal =
        transaction.journal_transaction_id &&
          typeof transaction.journal_transaction_id === "object"
          ? transaction.journal_transaction_id
          : {};

      const reference =
        transaction.transaction_no ||
        journal.transaction_no ||
        transaction.reference_no ||
        journal.reference_no ||
        transaction.reference_id ||
        transaction.invoice_no ||
        "-";

      const { debit, credit } = getTransactionAmounts(transaction);

      runningBalance += getNaturalMovement(debit, credit);

      return {
        id:
          transaction._id ||
          `${reference}-${transaction.line_no || index}`,
        date: formatShortDate(
          getTransactionDateValue(transaction)
        ),
        reference,
        description:
          transaction.description ||
          journal.description ||
          `${journal.transaction_type ||
          transaction.transaction_type ||
          "account"
          } transaction`,
        debit,
        credit,
        balance: runningBalance,
        sourceTransaction: transaction,
        isBalanceForward: false
      };
    });

    if (appliedFilter.start_date) {
      rows.unshift({
        id: `balance-forward-${appliedFilter.start_date}`,
        date: formatShortDate(
          `${appliedFilter.start_date}T00:00:00`
        ),
        reference: "BAL-FWD",
        description: "Balance Forward",
        debit: 0,
        credit: 0,
        balance: balanceForward,
        sourceTransaction: null,
        isBalanceForward: true
      });
    }

    return rows;
  }, [
    datedTransactions,
    balanceForward,
    appliedFilter.start_date,
    account
  ]);

  const displayLedgerData = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return ledgerData;
    }

    return ledgerData.filter((item) => {
      if (item.isBalanceForward) return true;

      return [
        item.description,
        item.reference,
        item.date
      ]
        .filter(Boolean)
        .some((value) =>
          String(value)
            .toLowerCase()
            .includes(normalizedSearch)
        );
    });
  }, [ledgerData, searchTerm]);

  const {
    totalDebit,
    totalCredit,
    endingBalance,
    completeLedgerBalance
  } = useMemo(() => {
    const periodTotals = datedTransactions.reduce(
      (totals, transaction) => {
        const { debit, credit } =
          getTransactionAmounts(transaction);

        return {
          debit: totals.debit + debit,
          credit: totals.credit + credit
        };
      },
      {
        debit: 0,
        credit: 0
      }
    );

    const allBalance = transactions.reduce(
      (balance, transaction) => {
        const { debit, credit } =
          getTransactionAmounts(transaction);

        return (
          balance +
          getNaturalMovement(debit, credit)
        );
      },
      0
    );

    const lastLedgerRow = [...ledgerData]
      .reverse()
      .find((item) => !item.isBalanceForward);

    return {
      totalDebit: periodTotals.debit,
      totalCredit: periodTotals.credit,
      endingBalance:
        lastLedgerRow?.balance ?? balanceForward,
      completeLedgerBalance: allBalance
    };
  }, [
    datedTransactions,
    transactions,
    ledgerData,
    balanceForward,
    account
  ]);

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={48} />
        <p>Loading account details...</p>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="error-container">
        <AlertCircle size={48} color="#f5222d" />
        <h3>Account Not Found</h3>
        <p>The requested account could not be found.</p>
        <button onClick={handleBack} className="back-button">
          <ArrowLeft size={16} /> Back to Accounts
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .account-transactions-container {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
          background: #f5f6f8;
          min-height: 100vh;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
            "Segoe UI", sans-serif;
          color: #1f2937;
        }

        .loading-container,
        .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 16px;
          text-align: center;
          background: white;
          border-radius: 12px;
          padding: 48px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .back-button,
        .back-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          cursor: pointer;
        }

        .back-button {
          background: #2e3192;
          color: white;
          border: none;
        }

        .view-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .header-actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          background: white;
          border: 1px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .action-btn[title="Print"] {
          background: #f1592a;
          color: white;
          border: none;
        }

        .action-btn[title="Download PDF"] {
          background: #2e3192;
          color: white;
          border: none;
        }

        .account-header {
          background: white;
          border-radius: 12px;
          padding: 24px 20px;
          margin-bottom: 24px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .account-header h1 {
          margin: 0 0 12px;
          font-size: 26px;
          color: #272a8f;
          letter-spacing: -0.3px;
        }

        .account-type-subtitle {
          margin: -4px 0 12px;
          color: #6b7280;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.2px;
        }

        .balance-value {
          font-size: 26px;
          font-weight: 750;
          margin-left: 8px;
        }

        .balance-value.normal {
          color: #2e3192;
        }

        .balance-value.abnormal {
          color: #dc3545;
        }

        .balance-value.zero {
          color: #6c757d;
        }

        .filters-section {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .filter-toggle-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          cursor: pointer;
          color: #2e3192;
        }

        .search-box {
          flex: 1;
          max-width: 400px;
          position: relative;
        }

        .search-box input {
          width: 100%;
          padding: 8px 32px 8px 36px;
          border: 1px solid #ddd;
          border-radius: 8px;
        }

        .search-box input:focus {
          outline: none;
          border-color: #2e3192;
          box-shadow: 0 0 0 2px rgba(46,49,146,0.2);
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }

        .clear-search {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
        }

        .filters-panel {
          background: white;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid #e0e0e0;
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          align-items: flex-end;
        }

        .filter-group {
          flex: 1;
          min-width: 150px;
        }

        .filter-group label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #666;
        }

        .filter-group input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
        }

        .filter-group input:focus {
          outline: none;
          border-color: #2e3192;
        }

        .filter-actions {
          display: flex;
          gap: 10px;
        }

        .apply-filters-btn,
        .reset-filters-btn {
          padding: 8px 20px;
          border-radius: 6px;
          cursor: pointer;
        }

        .apply-filters-btn {
          background: #2e3192;
          color: white;
          border: none;
        }

        .reset-filters-btn {
          background: #f5f5f5;
          border: 1px solid #ddd;
        }

        .table-container {
          background: white;
          border-radius: 12px;
          overflow-x: auto;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .ledger-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 14px;
        }

        .ledger-table th {
          background: #2e3192;
          color: white;
          padding: 13px 12px;
          font-weight: 700;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #23258a;
          white-space: nowrap;
        }

        .ledger-table td {
          padding: 12px;
          border-bottom: 1px solid #edf0f3;
          vertical-align: middle;
        }

        .col-index {
          width: 4%;
          text-align: center;
        }

        .col-date {
          width: 12%;
          white-space: nowrap;
        }

        .col-ref {
          width: 13%;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 12px;
          word-break: break-word;
        }

        .col-desc {
          width: 39%;
          word-break: break-word;
        }

        .col-in,
        .col-out,
        .col-balance {
          width: 10%;
          text-align: right;
          font-variant-numeric: tabular-nums;
        }

        .col-in.has-value,
        .col-out.has-value {
          color: #1f2937;
          font-weight: 700;
        }

        .col-balance.normal {
          color: #2e3192;
          font-weight: 700;
        }

        .col-balance.abnormal {
          color: #dc3545;
          font-weight: 700;
        }

        .col-balance.zero {
          color: #6c757d;
        }

        .ledger-total-bar {
          display: flex;
          justify-content: flex-end;
          gap: 28px;
          flex-wrap: wrap;
          padding: 16px 18px;
          background: #f8f9fa;
          border-top: 2px solid #e9ecef;
          font-variant-numeric: tabular-nums;
        }

        .ledger-summary-item {
          min-width: 145px;
          text-align: right;
        }

        .ledger-summary-label {
          display: block;
          margin-bottom: 4px;
          color: #6b7280;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.45px;
          text-transform: uppercase;
        }

        .ledger-summary-value {
          display: block;
          font-size: 14px;
          font-weight: 750;
        }

        .ledger-summary-value.total-in,
        .ledger-summary-value.total-out {
          color: #1f2937;
        }

        .ledger-summary-value.total-balance.normal {
          color: #272a8f;
        }

        .ledger-summary-value.total-balance.abnormal {
          color: #dc3545;
        }

        .ledger-summary-value.total-balance.zero {
          color: #6c757d;
        }

        .ledger-table tbody tr:nth-child(even) {
          background: #fbfcfd;
        }

        .ledger-table tbody tr:hover {
          background: #f3f5ff;
        }

        .account-balance {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 4px;
        }

        .balance-label {
          color: #6b7280;
          font-size: 14px;
        }


        .balance-forward-reference {
          color: #334155;
          font-weight: 800;
        }

        .ledger-table tbody tr.balance-forward-row {
          background: #eef3ff;
          border-top: 2px solid #c7d2fe;
          border-bottom: 2px solid #c7d2fe;
          font-weight: 700;
        }

        .reference-button {
          padding: 0;
          border: none;
          background: transparent;
          color: #2563a8;
          font: inherit;
          text-align: left;
          text-decoration: underline;
          cursor: pointer;
          overflow-wrap: anywhere;
        }

        .reference-button:hover {
          color: #1d4ed8;
        }

        .journal-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(2px);
        }

        .journal-modal {
          width: min(960px, 100%);
          max-height: 92vh;
          overflow: auto;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3);
        }

        .journal-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 20px 28px;
          background: #26364d;
          color: #fff;
          border-radius: 16px 16px 0 0;
        }

        .journal-modal-heading {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .journal-modal-icon,
        .journal-close-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.1);
        }

        .journal-modal-title {
          margin: 0;
          font-size: 21px;
          font-weight: 750;
        }

        .journal-modal-reference {
          margin-top: 2px;
          font-size: 13px;
          color: #d9e2ec;
        }

        .journal-close-button {
          border: none;
          color: #fff;
          cursor: pointer;
        }

        .journal-modal-body {
          padding: 30px;
        }

        .journal-modal-loading {
          min-height: 280px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
        }

        .journal-info-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .journal-info-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 18px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #fafbfc;
        }

        .journal-info-label {
          display: block;
          color: #6b7280;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .journal-info-value {
          display: block;
          margin-top: 2px;
          color: #1f2937;
          font-size: 18px;
          font-weight: 750;
          overflow-wrap: anywhere;
        }

        .journal-description-box {
          margin-bottom: 26px;
          padding: 16px 18px;
          border-left: 4px solid #3b82f6;
          border-radius: 10px;
          background: #eff6ff;
        }

        .journal-description-label {
          display: block;
          margin-bottom: 5px;
          color: #3b6fa5;
          font-size: 12px;
          font-weight: 750;
          text-transform: uppercase;
        }

        .journal-description-text {
          color: #243b5a;
          font-size: 16px;
          font-weight: 600;
        }

        .journal-lines-wrapper {
          width: 100%;
          overflow-x: auto;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
        }

        .journal-lines-table {
          width: 100%;
          min-width: 720px;
          table-layout: fixed;
          border-collapse: collapse;
        }

        .journal-lines-table thead {
          display: table-header-group !important;
        }

        .journal-lines-table tbody {
          display: table-row-group !important;
        }

        .journal-lines-table tr {
          display: table-row !important;
        }

        .journal-lines-table th,
        .journal-lines-table td {
          display: table-cell !important;
          padding: 15px 18px;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: middle;
        }

        .journal-lines-table th:nth-child(1),
        .journal-lines-table td:nth-child(1) {
          width: 22%;
          text-align: left;
        }

        .journal-lines-table th:nth-child(2),
        .journal-lines-table td:nth-child(2) {
          width: 48%;
          text-align: left;
        }

        .journal-lines-table th:nth-child(3),
        .journal-lines-table td:nth-child(3),
        .journal-lines-table th:nth-child(4),
        .journal-lines-table td:nth-child(4) {
          width: 15%;
          text-align: right !important;
          white-space: nowrap;
          font-variant-numeric: tabular-nums;
        }

        .journal-lines-table th {
          background: #f8fafc;
          color: #4b5563;
          font-size: 13px;
          font-weight: 700;
        }

        .journal-lines-table .amount-cell {
          text-align: right !important;
          white-space: nowrap;
          font-variant-numeric: tabular-nums;
          font-weight: 700;
        }

        .journal-account-name {
          font-weight: 750;
          color: #1f2937;
        }

        .journal-account-code {
          display: block;
          margin-top: 2px;
          color: #6b7280;
          font-size: 12px;
        }

        .journal-total-row td {
          border-top: 2px solid #334155;
          border-bottom: none;
          background: #f8fafc;
          font-weight: 800;
        }

        .journal-modal-footer {
          display: flex;
          justify-content: flex-end;
          padding: 18px 28px 26px;
        }

        .journal-close-details {
          padding: 11px 24px;
          border: none;
          border-radius: 9px;
          background: #26364d;
          color: #fff;
          font-weight: 700;
          cursor: pointer;
        }

        @media (max-width: 760px) {
          .journal-modal-overlay {
            padding: 10px;
          }

          .journal-modal-body {
            padding: 18px;
          }

          .journal-info-grid {
            grid-template-columns: 1fr;
          }
        }

        @media print {
          .view-header,
          .filters-section,
          .filters-panel {
            display: none !important;
          }

          .account-header,
          .table-container {
            box-shadow: none;
            border: 1px solid #ddd;
          }

          .ledger-table th {
            background: #e0e0e0 !important;
            color: black !important;
          }
        }
      `}</style>

      <div className="account-transactions-container">
        <div className="view-header">
          <button onClick={handleBack} className="back-btn">
            <ArrowLeft size={20} />
            <span>Back to Accounts</span>
          </button>

          <div className="header-actions">
            <button onClick={handlePrint} className="action-btn" title="Print">
              <Printer size={18} />
            </button>

            <button onClick={handleDownloadPDF} className="action-btn" title="Download PDF">
              <Download size={18} />
            </button>
          </div>
        </div>

        <div className="account-header">
          <h1>{account.account_name}</h1>
          <div className="account-type-subtitle">{getAccountTypeDisplay()}</div>

          <div className="account-balance">
            <span className="balance-label">Current Balance:</span>
            <span
              className={`balance-value ${getBalanceClass(
                completeLedgerBalance
              )}`}
            >
              {formatNaturalBalance(completeLedgerBalance)}
            </span>
          </div>
        </div>

        <div className="filters-section">
          <button className="filter-toggle-btn" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} /> {showFilters ? "Hide Filters" : "Show Filters"}
          </button>

          <div className="search-box">
            <Search size={16} />

            <input
              type="text"
              placeholder="Search by description or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm("")}>
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="start_date"
                value={filter.start_date}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="end_date"
                value={filter.end_date}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-actions">
              <button onClick={applyFilters} className="apply-filters-btn">
                Apply Filters
              </button>

              <button onClick={resetFilters} className="reset-filters-btn">
                <RefreshCw size={14} /> Reset
              </button>
            </div>
          </div>
        )}

        <div className="table-container">
          {loadingTransactions ? (
            <div className="loading-transactions">
              <Loader2 className="animate-spin" size={32} />
              <p>Loading transactions...</p>
            </div>
          ) : displayLedgerData.length === 0 ? (
            <div className="no-transactions">
              <History size={48} color="#ccc" />
              <p>No transactions found</p>
            </div>
          ) : (
            <>
              <table className="ledger-table">
                <thead>
                  <tr>
                    <th className="col-index">#</th>
                    <th className="col-date">DATE</th>
                    <th className="col-ref">REFERENCE</th>
                    <th className="col-desc">DESCRIPTION</th>
                    <th className="col-in">DEBIT</th>
                    <th className="col-out">CREDIT</th>
                    <th className="col-balance">RUNNING BALANCE</th>
                  </tr>
                </thead>

                <tbody>
                  {displayLedgerData.map((item, index) => (
                    <tr
                      key={item.id || index}
                      className={
                        item.isBalanceForward
                          ? "balance-forward-row"
                          : ""
                      }
                    >
                      <td className="col-index">
                        {item.isBalanceForward ? "" : index + 1}
                      </td>
                      <td className="col-date">{item.date}</td>
                      <td className="col-ref">
                        {item.isBalanceForward ? (
                          <span className="balance-forward-reference">
                            {item.reference}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="reference-button"
                            onClick={() =>
                              handleReferenceClick(
                                item.sourceTransaction
                              )
                            }
                            title="View journal transaction details"
                          >
                            {item.reference}
                          </button>
                        )}
                      </td>
                      <td className="col-desc">{item.description}</td>

                      <td
                        className={`col-in ${item.debit > 0 ? "has-value" : ""
                          }`}
                      >
                        {item.debit > 0
                          ? formatAccountCurrency(item.debit)
                          : "-"}
                      </td>

                      <td
                        className={`col-out ${item.credit > 0 ? "has-value" : ""
                          }`}
                      >
                        {item.credit > 0
                          ? formatAccountCurrency(item.credit)
                          : "-"}
                      </td>

                      <td
                        className={`col-balance ${getBalanceClass(
                          item.balance
                        )}`}
                      >
                        {formatNaturalBalance(item.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="ledger-total-bar">
                <div className="ledger-summary-item">
                  <span className="ledger-summary-label">Total Debits</span>
                  <span className="ledger-summary-value total-in">
                    {formatAccountCurrency(totalDebit)}
                  </span>
                </div>

                <div className="ledger-summary-item">
                  <span className="ledger-summary-label">Total Credits</span>
                  <span className="ledger-summary-value total-out">
                    {formatAccountCurrency(totalCredit)}
                  </span>
                </div>

                <div className="ledger-summary-item">
                  <span className="ledger-summary-label">Ending Balance</span>
                  <span
                    className={`ledger-summary-value total-balance ${getBalanceClass(
                      endingBalance
                    )}`}
                  >
                    {formatNaturalBalance(endingBalance)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

      </div>

      {showJournalModal && (
        <div
          className="journal-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeJournalModal();
          }}
        >
          <div
            className="journal-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Journal Entry Details"
          >
            <div className="journal-modal-header">
              <div className="journal-modal-heading">
                <span className="journal-modal-icon">
                  <FileText size={23} />
                </span>

                <div>
                  <h2 className="journal-modal-title">
                    Journal Transaction Details
                  </h2>
                  <div className="journal-modal-reference">
                    Reference:{" "}
                    {selectedJournal?.reference_no ||
                      selectedJournal?.transaction_no ||
                      "Loading..."}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="journal-close-button"
                onClick={closeJournalModal}
                aria-label="Close journal details"
              >
                <X size={23} />
              </button>
            </div>

            {loadingJournal ? (
              <div className="journal-modal-loading">
                <Loader2 className="animate-spin" size={36} />
                <p>Loading journal details...</p>
              </div>
            ) : journalError ? (
              <div className="journal-modal-loading">
                <AlertCircle size={42} color="#dc3545" />
                <h3 style={{ margin: 0 }}>
                  Unable to load journal details
                </h3>
                <p style={{ margin: 0, textAlign: "center" }}>
                  {journalError}
                </p>
                <button
                  type="button"
                  className="journal-close-details"
                  onClick={closeJournalModal}
                >
                  Close
                </button>
              </div>
            ) : selectedJournal ? (
              <>
                <div className="journal-modal-body">
                  <div className="journal-info-grid">
                    <div className="journal-info-card">
                      <Calendar size={20} />
                      <div>
                        <span className="journal-info-label">Date</span>
                        <span className="journal-info-value">
                          {formatShortDate(selectedJournal.transaction_date)}
                        </span>
                      </div>
                    </div>

                    <div className="journal-info-card">
                      <Hash size={20} />
                      <div>
                        <span className="journal-info-label">Reference</span>
                        <span className="journal-info-value">
                          {selectedJournal.reference_no ||
                            selectedJournal.transaction_no ||
                            "-"}
                        </span>
                      </div>
                    </div>

                    <div className="journal-info-card">
                      <User size={20} />
                      <div>
                        <span className="journal-info-label">Posted By</span>
                        <span className="journal-info-value">
                          {typeof selectedJournal.posted_by === "object"
                            ? selectedJournal.posted_by?.Usercomp_sure_name ||
                            selectedJournal.posted_by?.name ||
                            "System"
                            : selectedJournal.posted_by || "System"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="journal-description-box">
                    <span className="journal-description-label">
                      Description / Memo
                    </span>
                    <div className="journal-description-text">
                      {selectedJournal.description || "-"}
                    </div>
                  </div>

                  <div className="journal-lines-wrapper">
                    <table className="journal-lines-table">
                      <thead>
                        <tr>
                          <th className="journal-account-column">Account</th>
                          <th className="journal-description-column">Description</th>
                          <th className="journal-debit-column amount-cell">Debit</th>
                          <th className="journal-credit-column amount-cell">Credit</th>
                        </tr>
                      </thead>

                      <tbody>
                        {(selectedJournal.details || []).length > 0 ? (
                          (selectedJournal.details || []).map((line, index) => (
                            <tr key={line._id || index}>
                              <td className="journal-account-column">
                                <span className="journal-account-name">
                                  {getJournalLineAccountName(line)}
                                </span>

                                {getJournalLineAccountCode(line) && (
                                  <span className="journal-account-code">
                                    {getJournalLineAccountCode(line)}
                                  </span>
                                )}
                              </td>

                              <td className="journal-description-column">
                                {line.description ||
                                  selectedJournal.description ||
                                  "-"}
                              </td>

                              <td className="journal-debit-column amount-cell">
                                {Number(line.debit) > 0
                                  ? formatAccountCurrency(line.debit)
                                  : "-"}
                              </td>

                              <td className="journal-credit-column amount-cell">
                                {Number(line.credit) > 0
                                  ? formatAccountCurrency(line.credit)
                                  : "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center" }}>
                              No journal detail lines found
                            </td>
                          </tr>
                        )}

                        <tr className="journal-total-row">
                          <td colSpan="2" style={{ textAlign: "right" }}>
                            TOTAL
                          </td>

                          <td className="journal-debit-column amount-cell">
                            {formatAccountCurrency(getJournalTotals().debit)}
                          </td>

                          <td className="journal-credit-column amount-cell">
                            {formatAccountCurrency(getJournalTotals().credit)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="journal-modal-footer">
                  <button
                    type="button"
                    className="journal-close-details"
                    onClick={closeJournalModal}
                  >
                    Close Details
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};

export default AccountLedger;